import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { useEventUserRole } from '@shared/components/AuthProvider';

import { useGetCashFundsByEventIdQuery } from '../../CashFunds.graphql.generated';

/**
 * Determine whether Cash Funds should be visible for an event,
 * and whether to show a "New" pill badge.
 *
 * `visible` is `true` when either:
 * - the `newCashFundsEnabled` feature flag is on, OR
 * - the event has any cash funds (including archived)
 *
 * `hasNewPill` is `true` only when the feature flag is on — it indicates
 * the feature is being rolled out and should be highlighted.
 *
 * `hasFunds` is `true` only when there are actual cash funds on the event
 * (data-derived, independent of any feature flag). Use this when you need
 * to check fund existence without inheriting flag-based visibility.
 *
 * Feature flags gate *discovery* (showing the entry point to new users),
 * not functionality — admins who created funds must always be able to
 * manage them (including archived ones), and guests must always see
 * configured content.
 *
 * NOTE: Reuses the full GetCashFundsByEventId query with `cache-first`
 * so that cached data is available when the user navigates to the
 * Cash Funds dashboard.
 */
export function useHasCashFunds(eventId: string | undefined): {
  visible: boolean;
  hasNewPill: boolean;
  hasFunds: boolean;
} {
  // GetCashFundsByEventId is admin-gated (@cashFundAuth(role: admin)).
  // Firing it for anonymous viewers (bots, signed-out users) guarantees a
  // "Missing authorization context" error per page load, so skip unless the
  // viewer is a signed-in admin of the event.
  const { isAdmin, hasIdentifiedUserOnce, eventUserEventId } = useEventUserRole();

  // The role outlives the event it was resolved for: an identify refetch keeps the previous
  // activeEventUser, so across an event-handle switch isAdmin still describes the event being
  // left. Comparing event IDs catches that without gating on `fetching`, which is also true for
  // same-event token refreshes and would drop the entry point mid-session. An unknown eventId
  // confirms nothing, so it does not count as a match.
  const isRoleFromAnotherEvent = Boolean(eventUserEventId && eventUserEventId !== eventId);
  const isEventAdmin = Boolean(eventId) && hasIdentifiedUserOnce && isAdmin && !isRoleFromAnotherEvent;

  // Skipped for non-admins so anonymous page loads (crawlers included) don't record an
  // Amplitude exposure for a viewer who can never see the entry point.
  const [isNewCashFundsEnabled] = useIsFeatureEnabled('newCashFundsEnabled', { skip: !isEventAdmin });

  const { data } = useGetCashFundsByEventIdQuery({
    variables: { eventId: eventId ?? '' },
    skip: !isEventAdmin,
    fetchPolicy: 'cache-first',
    batchMode: 'fast',
  });

  const funds = data?.getCashFundsByEventId;

  const hasFunds = Array.isArray(funds) && funds.length > 0;

  if (isEventAdmin && isNewCashFundsEnabled) {
    return { visible: true, hasNewPill: true, hasFunds };
  }

  return { visible: hasFunds, hasNewPill: false, hasFunds };
}
